import { Divider, Dropdown, message } from "antd";
import { useState, useEffect } from "react";
import GenericDeleteModal from "../../../components/buttons/genericDeleteModal";
import HideOnMobile from "../../../components/generic/hideOnMobile";
import AddOrEditReportButton from "./AddOrEditReportButton";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid";
import { Link } from "react-router-dom";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import MoreVert from "@mui/icons-material/MoreVert";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";
import PermissionGate from '../../../helpers/PermissionGate'

import {
	groupingTypes,
	typeFilter,
	groupingTypeFilter,
	transactionGroupingTypeFilter,
	groupKeyToLabelMap,
	isTransactionReportType,
} from "./constants";


const Reports = () => {
	// reports data
	const [reports, setReports] = useState([]);
	const [loading, setLoading] = useState(true);
	const [reportsCount, setReportsCount] = useState(-1);

	// new report
	const [groupingType, setGroupingType] = useState(null);
	const [selectGroups, setSelectGroups] = useState(null);
	const [reportType, setReportType] = useState(null);
	const [selectGroupFilter, setSelectGroupFilter] = useState([]);
	const [transactionGroupingType, setTransactionGroupingType] = useState(null);
	const [groupingOptionsCache, setGroupingOptionsCache] = useState({});
	const [loadingGroupingOptions, setLoadingGroupingOptions] = useState({ 'BA': true, 'PU': true, 'EN': true, 'HI': true, 'AA': true });

	
	useEffect(() => {
		if (!groupingType) {
			setSelectGroupFilter([]);
			setSelectGroups([]);
			return;
		}
		if (groupingOptionsCache[groupingType]) {
			setSelectGroupFilter(groupingOptionsCache[groupingType]);
		} else {
			setSelectGroupFilter([]);
		}
		
		setSelectGroups([]);
	}, [groupingType, groupingOptionsCache]);

	useEffect(() => {
		groupingTypes.map((type) => {
		  if (!groupingOptionsCache[type]) {
			setLoadingGroupingOptions(prev => ({ ...prev, [type]: true }));
			try {
				backendGetRequestWithCache(
				"/api/landlord/grouping-type-filter-options/",
				{ params: { grouping_type: type } },
				(cachedResponse) => {
				  if (cachedResponse?.data?.data && !groupingOptionsCache[type]) {
					setGroupingOptionsCache(prev => ({ ...prev, [type]: cachedResponse.data.data }));
				  }
				},
				(nonCachedResponse) => {
				  if (nonCachedResponse?.data?.data) {
					setGroupingOptionsCache(prev => ({ ...prev, [type]: nonCachedResponse.data.data }));
				  }
				},
				(error) => {
				  console.error(`Error preloading grouping options for ${type}:`, error);
				}
			  );
			} catch (error) {
			  console.error(`Error preloading grouping options for ${type}:`, error);
			} finally {
			  setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
			}
		  }
		});
	}, []);

	useEffect(() => {
		return () => {
			setLoadingGroupingOptions({});
		};
	}, []);

	useEffect(() => {
		if (reportsCount < 0) return;
		setLoading(reportsCount > reports.length);
	}, [reports, reportsCount]);

	useEffect(() => {
		backendGetRequest("/api/reports/", { params: { page_index: 0 } })
			.then((res) => {
				setReportsCount(res?.data?.total_records);
				for (let i = 1; i <= res?.data?.total_pages; i++) {
					backendGetRequestWithCache("/api/reports/", { params: { page_index: i } },
						(cachedResponse) => {
							if (cachedResponse.data) {
								setReports(prevState => {
									const newReports = Array.isArray(cachedResponse?.data?.data) ? cachedResponse?.data?.data : [];
									const filteredReports = newReports.filter(newReport =>
										!prevState.some(prevReports => prevReports.id === newReport.id)
									);
									return [...prevState, ...filteredReports];
								});
							}
						},
						(nonCachedResponse) => {
							if (nonCachedResponse.data) {
								setReports(prevState => {
									const updatedReports = [...prevState];
									const newReports = Array.isArray(nonCachedResponse?.data?.data) ? nonCachedResponse?.data?.data : [];
									newReports.forEach(newReport => {
										const index = prevState.findIndex(prevReport => prevReport.id === newReport.id);
										if (index !== -1) {
											updatedReports[index] = newReport;
										} else {
											updatedReports.push(newReport);
										}
									});
									return updatedReports;
								});
							}
						});
				}
			});
	}, []);

	const deleteReport = (reportId) => {
		setReports(prevState => prevState.filter(report => report.id !== reportId));
		setReportsCount(prevState => prevState - 1);
	}

	const handleAddReport = (report) => {
		setReports(prevState => [...prevState, report]);
	}

	const handleEditReport = (updatedReport) => {
		setReports((prevState) =>
			prevState.map((report) =>
				report.id === updatedReport.id ? updatedReport : report
			)
		);
	};

	const columns = [
		{
			field: 'name',
			headerName: 'Report',
			flex: 1.5,
			sortable: true,
			renderCell: (params) => (
				<Link to={`/reports/details/000${params.row.id}`}>{params.value}</Link>
			),
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			sortable: true,
			filterable: true,
			renderCell: (params) => typeFilter.find((filter) => filter.value === params.value)?.label || params.value,
		},
		{
			field: 'group_name',
			headerName: 'Account Grouping',
			flex: 1.25,
			sortable: true,
			renderCell: (params) => (
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<div style={{ fontSize: '1rem', color: '#888', marginRight: '10px' }}>
						{groupKeyToLabelMap[params?.row?.grouping_type]}
					</div>
					<div style={{ marginBottom: 12.5 }}>
						{params?.row?.group_name}
					</div>
				</div>
			),
		},
		{
			field: 'date_created',
			headerName: 'Date Created',
			flex: 0.75,
			sortable: true,
			renderCell: (params) => <span>{params?.value?.slice(0, 10)}</span>,
		},
		{
			field: 'actions',
			headerName: '',
			flex: 0.1,
			align: 'right',
			renderCell: (params) => (
				<HideOnMobile>
					<PermissionGate requiredRole='dashboardEditor' >
						<Dropdown
							overlay={
								<div style={{ backgroundColor: 'white', padding: '10px' }}>
									<AddOrEditReportButton
										edit={true}
										name={params.row.name}
										type={typeFilter.find(type => type.label === params.row.type).value || params.row.type}
										groupingType={params.row.grouping_type}
										groupName={params.row.group_name}
										startDate={params.row.start_date}
										endDate={params.row.end_date}
										numAccounts={params.row.num_accounts}
										reportId={params.row.id}
										onSubmit={handleEditReport}
										reportType={reportType}
										reportTypes={typeFilter}
										setReportType={setReportType}
										groupingTypeFilter={groupingTypeFilter}
										selectGroupFilter={selectGroupFilter}
										selectGroups={selectGroups}
										setGroupingType={setGroupingType}
										setSelectGroups={setSelectGroups}
										setTransactionGroupingType={setTransactionGroupingType}
										isTransactionReportType={isTransactionReportType}
										transactionGroupingTypeFilter={transactionGroupingTypeFilter}
										loadingGroupingOptions={!groupingType || loadingGroupingOptions[groupingType]}
									/>
									<Divider style={{ margin: '5px 0px' }} />
									<GenericDeleteModal
										type='Report'
										title={`Are you sure you want to delete ${params.row.name}?`}
										URL="/api/reports/"
										deleteParams={{ report_id: params.row.id }}
										description={`By clicking Delete below you will no longer have the report ${params.row.name} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
										handleDeleteSuccess={() => deleteReport(params.row.id)}
										handleDeleteFailure={() => message.error(`Failed to delete the report ${params.row.name}`)}
									/>
								</div>
							}
							placement="bottomLeft"
							arrow
						>
							<MoreVert fontSize="inherit" />
						</Dropdown>
					</PermissionGate>
				</HideOnMobile>
			),
		},
	];

	return (
		<OuterBox>
			<HeaderBox
				title={"Reports"}
				titleFontSize={16}
				actions={
					<AddOrEditReportButton
						onSubmit={handleAddReport}
						reportType={reportType}
						reportTypes={typeFilter}
						setReportType={setReportType}
						groupingTypeFilter={groupingTypeFilter}
						selectGroupFilter={selectGroupFilter}
						groupingType={groupingType}
						selectGroups={selectGroups}
						setGroupingType={setGroupingType}
						setSelectGroups={setSelectGroups}
						setTransactionGroupingType={setTransactionGroupingType}
						isTransactionReportType={isTransactionReportType}
						transactionGroupingTypeFilter={transactionGroupingTypeFilter}
						loadingGroupingOptions={!groupingType || loadingGroupingOptions[groupingType]}
					/>}
			/>

			<TableWithLoadingWrapper
				pagination={false}
				columns={columns}
				dataSource={reports}
				rowKey={(record) => record.id}
				tableLayout={"fixed"}
				style={{ width: "100%" }}
				scroll={{
					x: 600,
				}}
				sortModel={[
					{
						field: "name",
						sort: "asc",
					},
				]}
				loading={loading}
			/>
		</OuterBox>
	);
};

export default Reports;
